import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';
import React, { useCallback, useState } from 'react';

import { MobileLabel } from '../../Typography';
import { runValidationRules } from '../validation';
import type { MobileSelectProps } from './types';

const selectSx: SxProps<Theme> = {
  borderRadius: 2,
  fontSize: '0.9375rem',
  fontWeight: 500,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
  },
};

const MobileSelect: React.FC<MobileSelectProps> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Seleccionar...',
  disabled = false,
  required = false,
  error = false,
  helperText,
  validationRules,
  onBlur,
  fullWidth = true,
  sx,
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const validate = useCallback(
    (val: number | null) => runValidationRules(val, validationRules),
    [validationRules],
  );

  const handleChange = (event: SelectChangeEvent<number | ''>) => {
    const next =
      event.target.value === '' ? null : (event.target.value as number);
    onChange(next);
    if (validationRules?.length && touched) {
      setInternalError(validate(next));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (validationRules?.length) {
      setInternalError(validate(value));
    }
    onBlur?.();
  };

  const showError = error || (touched && internalError != null);
  const displayHelperText = helperText ?? internalError ?? '';

  return (
    <FormControl
      fullWidth={fullWidth}
      disabled={disabled}
      error={showError}
      sx={{ ...sx }}
    >
      {label != null && label !== '' && (
        <Box sx={{ mb: 0.75 }}>
          <MobileLabel component="span" sx={{ color: 'text.primary' }}>
            {label}
            {required && ' *'}
          </MobileLabel>
        </Box>
      )}
      <Select<number | ''>
        value={value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        displayEmpty
        renderValue={(selected) => {
          if (selected === '') return placeholder;
          const option = options.find((o) => o.id === selected);
          return option?.name ?? placeholder;
        }}
        IconComponent={ArrowDropDown}
        sx={selectSx}
        variant="outlined"
      >
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt.id} value={opt.id}>
            {opt.name}
          </MenuItem>
        ))}
      </Select>
      {showError && displayHelperText && (
        <MobileLabel
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'error.main',
            mt: 0.5,
            display: 'block',
          }}
        >
          {displayHelperText}
        </MobileLabel>
      )}
    </FormControl>
  );
};

export default MobileSelect;
export type {
  MobileSelectOption,
  MobileSelectProps,
  ValidationRule,
} from './types';
